import React, { useState, useRef, useEffect } from 'react';

interface SelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  label?: string;
  error?: string;
  helperText?: string;
  children: React.ReactNode;
}

export const Select: React.FC<SelectProps> = ({
  label,
  error,
  helperText,
  className = '',
  id,
  value,
  defaultValue,
  ...props
}) => {
  const selectId = id || `select-${Math.random().toString(36).substr(2, 9)}`;
  const [isFocused, setIsFocused] = useState(false);
  const [hasValue, setHasValue] = useState(false);
  const selectRef = useRef<HTMLSelectElement>(null);

  // Determine if label should float
  const currentValue = value !== undefined ? value : defaultValue;
  const shouldFloat = isFocused || hasValue || (currentValue !== undefined && currentValue !== '' && currentValue !== null);

  useEffect(() => {
    // Check initial value
    if (selectRef.current) {
      setHasValue(selectRef.current.value !== '');
    }
  }, [currentValue]);

  const handleFocus = (e: React.FocusEvent<HTMLSelectElement>) => {
    setIsFocused(true);
    if (props.onFocus) {
      props.onFocus(e);
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLSelectElement>) => {
    setIsFocused(false);
    setHasValue(e.target.value !== '');
    if (props.onBlur) {
      props.onBlur(e);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setHasValue(e.target.value !== '');
    if (props.onChange) {
      props.onChange(e);
    }
  };

  return (
    <div className="w-full">
      <div className="relative">
        {label && (
          <label
            htmlFor={selectId}
            className={`
              absolute left-0 transition-all duration-200 pointer-events-none
              ${shouldFloat
                ? 'top-0 text-xs text-blue-600 font-medium transform -translate-y-1'
                : 'top-3 text-sm text-gray-500'
              }
              ${error ? 'text-red-600' : ''}
            `}
          >
            {label}
            {props.required && <span className="text-red-500 ml-1">*</span>}
          </label>
        )}
        <select
          ref={selectRef}
          id={selectId}
          value={value}
          defaultValue={defaultValue}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onChange={handleChange}
          className={`
            w-full pt-6 pb-2 px-0 bg-transparent
            border-0 border-b-2 transition-all duration-200
            focus:outline-none focus:ring-0 appearance-none
            ${error 
              ? 'border-red-500 focus:border-red-600' 
              : 'border-gray-300 focus:border-blue-600'
            }
            ${shouldFloat ? 'text-gray-900' : 'text-gray-500'}
            ${className}
          `}
          {...props}
        >
          {props.children}
        </select>
        {/* Custom dropdown arrow */}
        <div className={`absolute right-0 pointer-events-none transition-all duration-200 ${shouldFloat ? 'top-3' : 'top-6'}`}>
          <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>
      {error && (
        <p className="mt-1 text-sm text-red-600">{error}</p>
      )}
      {helperText && !error && (
        <p className="mt-1 text-sm text-gray-500">{helperText}</p>
      )}
    </div>
  );
};

